import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  Menu, Home, MapPinPlus, ListChecks, FileText, Wallet, Gift, Bell, LayoutDashboard, AlertTriangle, Network,
  FolderCheck, Map, ReceiptText, ShieldCheck, Boxes, Smartphone, LogOut, Phone, Coins, Landmark,
  Users, CalendarDays, ClipboardList, BarChart3, Sun, Moon,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useUserLocation } from "@/lib/location";
import { LocationChip, LocationBanner } from "@/components/LocationChip";

const NAV = {
  ciudadano: [
    { to: "/app", label: "Inicio", icon: Home, end: true },
    { to: "/app/reportar", label: "Nuevo reporte", icon: MapPinPlus },
    { to: "/app/reportes", label: "Mis reportes", icon: ListChecks },
    { to: "/app/comunidad", label: "Comunidad", icon: Users },
    { to: "/app/eventos", label: "Eventos", icon: CalendarDays },
    { to: "/app/encuestas", label: "Encuestas", icon: ClipboardList },
    { to: "/app/tramites", label: "Ventanilla de trámites", icon: FileText },
    { to: "/app/pagos", label: "Pagos y domiciliación", icon: Wallet },
    { to: "/app/recompensas", label: "Recompensas HGT", icon: Gift },
    { to: "/app/notificaciones", label: "Notificaciones", icon: Bell },
  ],
  funcionario: [
    { to: "/app/intranet", label: "Tablero ejecutivo", icon: LayoutDashboard },
    { to: "/app/incidencias", label: "Panel de solicitudes", icon: AlertTriangle },
    { to: "/app/tareas", label: "Tareas interdependencias", icon: Network },
    { to: "/app/expedientes", label: "Expedientes", icon: FolderCheck },
    { to: "/app/analitica", label: "Análisis", icon: BarChart3 },
    { to: "/app/eventos-admin", label: "Gestión de eventos", icon: CalendarDays },
    { to: "/app/encuestas-admin", label: "Gestión de encuestas", icon: ClipboardList },
    { to: "/app/cobranza", label: "Cobranza en campo", icon: Map },
    { to: "/app/multas", label: "Multas", icon: ReceiptText },
    { to: "/app/auditoria", label: "Auditoría legal", icon: ShieldCheck },
    { to: "/app/notificaciones", label: "Notificaciones", icon: Bell },
  ],
  agente: [
    { to: "/app/campo", label: "Emitir multa", icon: Smartphone },
    { to: "/app/multas", label: "Boletas emitidas", icon: ReceiptText },
    { to: "/app/cobranza", label: "Ruta y adeudos", icon: Map },
    { to: "/app/incidencias", label: "Panel de solicitudes", icon: AlertTriangle },
    { to: "/app/tareas", label: "Tareas", icon: Network },
    { to: "/app/auditoria", label: "Auditoría legal", icon: ShieldCheck },
  ],
};
const ROLE_LABEL = { ciudadano: "Ciudadano", funcionario: "Funcionario", agente: "Agente de campo" };

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-2 pt-2">
      <span className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-[#7fd6e2] text-[#06151D] shadow-[0_0_24px_-4px_rgba(95,211,224,0.8)]"><Landmark className="h-5 w-5" /></span>
      <div className="leading-tight text-white">
        <div className="font-display font-semibold text-lg tracking-tight">HitchGob <span className="text-[#7fd6e2] font-mono text-[11px] align-middle ml-1">elOS</span></div>
        <div className="text-[10px] tracking-[0.18em] uppercase text-[#a9d6de] font-mono">Quintana Roo · OS</div>
      </div>
    </div>
  );
}

function NavList({ role, onNavigate }) {
  const items = [...(NAV[role] || NAV.ciudadano), { to: "/app/arquitectura", label: "Arquitectura y API", icon: Boxes }];
  return (
    <nav className="flex flex-col gap-0.5" data-testid="sidebar-nav">
      <div className="px-3 pb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#7fd6e2]/60">{ROLE_LABEL[role] || "Módulos"}</div>
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate}
          data-testid={`nav-${it.to.split("/").pop() || "inicio"}`}
          className={({ isActive }) =>
            `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ${isActive
              ? "bg-[rgba(95,211,224,0.12)] text-white font-medium shadow-[inset_0_0_0_1px_rgba(127,214,226,0.28)]"
              : "text-[#c9e6eb]/85 hover:bg-white/5 hover:text-white"}`}>
          {({ isActive }) => (
            <>
              <span className={`absolute left-0 top-2 bottom-2 w-[3px] rounded-full transition-opacity duration-150 ${isActive ? "bg-[#5fd3e0] opacity-100 shadow-[0_0_10px_#5fd3e0]" : "opacity-0"}`} />
              <it.icon className={`h-4 w-4 ${isActive ? "text-[#7fd6e2]" : "text-[#a9d6de]/70 group-hover:text-[#7fd6e2]"}`} /> {it.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function ThemeToggle({ className = "" }) {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" onClick={toggle} className={`relative ${className}`} data-testid="theme-toggle-button" aria-label={dark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}>
            {dark ? <Sun className="h-5 w-5 text-[#7fd6e2]" /> : <Moon className="h-5 w-5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{dark ? "Modo claro" : "Modo oscuro"}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function AppShell() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [phone, setPhone] = useState(user.phone || "");
  const notifs = useFetch("/notifications/mine", { poll: 6000 });
  const unread = notifs.data?.unread || 0;
  const loc = useUserLocation();
  // Solicita el permiso de ubicación del navegador al entrar a la plataforma
  useEffect(() => { if (loc.status === "prompt" || loc.status === "idle") loc.request(); }, [loc.status]); // eslint-disable-line

  const changeRole = async (role) => {
    try {
      const { data } = await api.patch("/auth/role", { role });
      setUser(data);
      toast.success(`Rol DEMO: ${ROLE_LABEL[role]}`);
      navigate("/app");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveProfile = async () => {
    try {
      const { data } = await api.patch("/auth/profile", { phone });
      setUser(data);
      setProfileOpen(false);
      toast.success("Número de WhatsApp guardado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sideCls = "bg-[#0B3540] dark:bg-[rgba(6,24,32,0.82)] dark:backdrop-blur-xl border-r border-[rgba(127,214,226,0.14)]";

  return (
    <div className="app-bg min-h-screen relative">
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 z-40 w-72 flex-col gap-7 p-4 overflow-y-auto ${sideCls}`}>
        <div className="pointer-events-none absolute inset-0 tech-grid opacity-40" />
        <div className="relative flex flex-col gap-7 flex-1">
          <Brand />
          <NavList role={user.role} />
          <SidebarLocation />
          <div className="rounded-lg border border-[rgba(127,214,226,0.16)] bg-black/20 p-3 text-xs text-[#c9e6eb]">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7fd6e2]/80 mb-1">Modo</div>
            Integraciones externas <b>SIMULADAS</b>. Datos con etiqueta <b>DEMO</b> son de ejemplo.
          </div>
        </div>
      </aside>

      <div className="lg:pl-72 relative z-10">
        <header className="sticky top-0 z-30 h-14 border-b border-[hsl(var(--border))] dark:border-[rgba(127,214,226,0.14)] bg-white/80 dark:bg-[rgba(6,24,32,0.72)] backdrop-blur-xl flex items-center gap-3 px-4 md:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className={`w-72 border-0 p-4 flex flex-col gap-6 text-white ${sideCls}`}>
              <SheetTitle className="sr-only">Menú</SheetTitle>
              <Brand />
              <NavList role={user.role} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="lg:hidden font-display font-semibold">HitchGob</div>
          <div className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-[#2EE6C8]" />Sistema en línea
          </div>

          <div className="ml-auto flex items-center gap-2">
            <LocationChip />
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">Rol <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">DEMO</span></div>
            <Select value={user.role} onValueChange={changeRole}>
              <SelectTrigger className="w-[150px] sm:w-[170px] h-9" data-testid="role-switcher-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ciudadano" data-testid="role-option-ciudadano">Ciudadano</SelectItem>
                <SelectItem value="funcionario" data-testid="role-option-funcionario">Funcionario</SelectItem>
                <SelectItem value="agente" data-testid="role-option-agente">Agente de campo</SelectItem>
              </SelectContent>
            </Select>
            <ThemeToggle />
            <Button variant="ghost" size="icon" className="relative" onClick={() => navigate("/app/notificaciones")} data-testid="topbar-notifications-button">
              <Bell className="h-5 w-5" />
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#FF5A6A] text-white text-[10px] flex items-center justify-center px-1 shadow-[0_0_10px_rgba(255,90,106,0.7)]" data-testid="notifications-unread-count">{unread}</span>}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full ring-1 ring-[rgba(127,214,226,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]" data-testid="user-menu-button">
                  <Avatar className="h-8 w-8"><AvatarImage src={user.picture} /><AvatarFallback>{(user.name || "U")[0]}</AvatarFallback></Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-white">
                <DropdownMenuLabel>
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/recompensas")}><Coins className="h-4 w-4 mr-2" />{user.tokens} HGT</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="user-menu-whatsapp"><Phone className="h-4 w-4 mr-2" />WhatsApp: {user.phone || "sin número"}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" />Cerrar sesión</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <LocationBanner />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 fade-up">
          <Outlet />
        </main>
      </div>

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Tu número de WhatsApp</DialogTitle>
            <DialogDescription>Recibirás confirmaciones de folio, cambios de estatus y pagos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="phone">Número (formato internacional)</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+529981234567" data-testid="profile-phone-input" />
          </div>
          <Button onClick={saveProfile} data-testid="profile-phone-save-button">Guardar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SidebarLocation() {
  const { status, coords, place, request } = useUserLocation();
  return (
    <button onClick={request} className="mt-auto text-left rounded-lg border border-[rgba(127,214,226,0.2)] bg-[rgba(95,211,224,0.06)] hover:bg-[rgba(95,211,224,0.12)] transition-colors duration-150 p-3 relative overflow-hidden" data-testid="sidebar-location">
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#a9d6de] font-mono flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${coords ? "bg-[#2EE6C8] live-dot" : "bg-[#F2C14E]"}`} />Tu ubicación</div>
      <div className="text-sm text-white font-medium mt-1 truncate">{coords ? (place?.suburb || place?.city || "Ubicación activa") : status === "denied" ? "Permiso bloqueado" : "Activar ubicación"}</div>
      <div className="text-[11px] text-[#c9e6eb] font-mono truncate">{coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)} · ±${coords.accuracy} m` : "Toca para permitir"}</div>
    </button>
  );
}

export { ROLE_LABEL };
